import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Briefcase,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  UserRound,
  X,
} from "lucide-react";
import logoSrc from "../../assets/brand.svg";
import HeaderNotificationBell from "../../components/HeaderNotificationBell";
import NavProfileAvatar from "../../components/NavProfileAvatar";
import { useNd1Session } from "../Nd1SessionContext";
import { getNd1SignupHref, ND1_HOME, ND1_SIGNIN } from "../nd1Paths";

function isHiringUser(currentUser) {
  return currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter";
}

function isApplicantUser(currentUser) {
  return currentUser?.role === "applicant";
}

function resolveWorkspacePath(currentUser) {
  if (isHiringUser(currentUser)) return "/hiring-dashboard";
  if (isApplicantUser(currentUser)) return "/applicant-home";
  return ND1_HOME;
}

function resolveProfilePath(currentUser) {
  if (isHiringUser(currentUser)) return "/recruitment-profile";
  if (isApplicantUser(currentUser)) return "/applicant-profile";
  return resolveWorkspacePath(currentUser);
}

function applicantNavLinkClass({ isActive }) {
  return `nd1-nav__link${isActive ? " nd1-nav__link--active" : ""}`;
}

function hiringNavLinkClass({ isActive }) {
  return `nd1-nav__link nd1-nav__link--p2${isActive ? " nd1-nav__link--active" : ""}`;
}

/**
 * Shared top navigation — public links for guests; workspace shortcuts when signed in.
 */
export default function Nd1SiteNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser, setCurrentUser, authToken } = useNd1Session();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHiringPage =
    pathname.startsWith("/hiring-managers") || pathname.startsWith("/how-it-works/hiring");
  const isJobSeekersPage =
    pathname.startsWith("/job-seekers") || pathname.startsWith("/how-it-works/applying");
  const isSigninPage = pathname.startsWith("/signin");
  const signupHref = getNd1SignupHref(pathname);
  const hasActiveSession = Boolean(currentUser && typeof currentUser === "object" && currentUser.email);
  const showApplicantWorkspace = hasActiveSession && isApplicantUser(currentUser);
  const showHiringWorkspace = hasActiveSession && isHiringUser(currentUser);
  const showPublicLinks = !showApplicantWorkspace && !showHiringWorkspace;
  const profilePath = resolveProfilePath(currentUser);
  const workspacePath = resolveWorkspacePath(currentUser);

  const navModeClass = showApplicantWorkspace
    ? " nd1-applicant-nav"
    : showHiringWorkspace
      ? " nd1-hiring-nav"
      : "";
  const navInnerClass = showApplicantWorkspace
    ? " nd1-applicant-nav__inner"
    : showHiringWorkspace
      ? " nd1-hiring-nav__inner"
      : "";
  const menuCloseBreakpoint = showApplicantWorkspace || showHiringWorkspace ? "900px" : "600px";
  const navAriaLabel = showApplicantWorkspace
    ? "Applicant workspace"
    : showHiringWorkspace
      ? "Hiring workspace"
      : "Primary";

  const avatarAriaLabel = useMemo(() => {
    if (showHiringWorkspace) return "Open recruitment profile";
    if (showApplicantWorkspace) return "Open your profile";
    return "Open your home page";
  }, [showApplicantWorkspace, showHiringWorkspace]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(`(min-width: ${menuCloseBreakpoint})`);
    const onChange = () => {
      if (mediaQuery.matches) closeMenu();
    };
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, [closeMenu, menuCloseBreakpoint]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, closeMenu]);

  const onSignOut = () => {
    setCurrentUser(null);
    closeMenu();
    navigate(ND1_HOME);
  };

  const sessionControls = (
    <>
      <HeaderNotificationBell currentUser={currentUser} authToken={authToken} />
      <NavProfileAvatar
        currentUser={currentUser}
        onClick={() => {
          closeMenu();
          navigate(showPublicLinks ? workspacePath : profilePath);
        }}
        ariaLabel={avatarAriaLabel}
      />
      <button
        type="button"
        className={
          showApplicantWorkspace
            ? "nd1-applicant-nav__sign-out"
            : showHiringWorkspace
              ? "nd1-hiring-nav__sign-out"
              : "nd1-nav__sign-out"
        }
        onClick={onSignOut}
      >
        <LogOut
          className={
            showApplicantWorkspace || showHiringWorkspace
              ? showApplicantWorkspace
                ? "nd1-applicant-nav__icon"
                : "nd1-hiring-nav__icon"
              : "nd1-nav__sign-out-icon"
          }
          aria-hidden="true"
        />
        <span
          className={
            showApplicantWorkspace
              ? "nd1-applicant-nav__sign-out-label"
              : showHiringWorkspace
                ? "nd1-hiring-nav__sign-out-label"
                : "nd1-nav__sign-out-label"
          }
        >
          Sign out
        </span>
      </button>
    </>
  );

  return (
    <header className={`nd1-nav${navModeClass}${menuOpen ? " nd1-nav--menu-open" : ""}`}>
      <nav className={`nd1-shell nd1-nav__inner${navInnerClass}`} aria-label={navAriaLabel}>
        <Link to={ND1_HOME} className="nd1-logo" onClick={closeMenu}>
          <img src={logoSrc} alt="JOBUWIN" className="nd1-logo__image" />
        </Link>

        <button
          type="button"
          className={`nd1-nav__menu-toggle${
            showApplicantWorkspace
              ? " nd1-applicant-nav__menu-toggle"
              : showHiringWorkspace
                ? " nd1-hiring-nav__menu-toggle"
                : ""
          }`}
          aria-expanded={menuOpen}
          aria-controls="nd1-nav-links"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? (
            <X className="nd1-nav__menu-toggle-icon" aria-hidden="true" />
          ) : (
            <Menu className="nd1-nav__menu-toggle-icon" aria-hidden="true" />
          )}
        </button>

        <div
          id="nd1-nav-links"
          className={
            showApplicantWorkspace
              ? "nd1-applicant-nav__menu"
              : showHiringWorkspace
                ? "nd1-hiring-nav__menu"
                : "nd1-nav__links"
          }
        >
          {showApplicantWorkspace ? (
            <>
              <div className="nd1-nav__links nd1-applicant-nav__links">
                <NavLink to="/applicant-home" end className={applicantNavLinkClass} onClick={closeMenu}>
                  <LayoutDashboard className="nd1-applicant-nav__icon" aria-hidden="true" />
                  Dashboard
                </NavLink>
                <NavLink to="/job-search" className={applicantNavLinkClass} onClick={closeMenu}>
                  <Search className="nd1-applicant-nav__icon" aria-hidden="true" />
                  Job search
                </NavLink>
                <NavLink to="/applicant/saved-jobs" className={applicantNavLinkClass} onClick={closeMenu}>
                  <Heart className="nd1-applicant-nav__icon" strokeWidth={1.75} fill="currentColor" aria-hidden="true" />
                  Saved jobs
                </NavLink>
                <NavLink to="/applicant-profile" className={applicantNavLinkClass} onClick={closeMenu}>
                  <UserRound className="nd1-applicant-nav__icon" aria-hidden="true" />
                  Profile
                </NavLink>
              </div>
              <div className="nd1-applicant-nav__session">{sessionControls}</div>
            </>
          ) : showHiringWorkspace ? (
            <>
              <div className="nd1-nav__links nd1-hiring-nav__links">
                <NavLink to="/hiring-dashboard" end className={hiringNavLinkClass} onClick={closeMenu}>
                  <LayoutDashboard className="nd1-hiring-nav__icon" aria-hidden="true" />
                  Dashboard
                </NavLink>
                <NavLink to="/hiring/jobs/new" className={hiringNavLinkClass} onClick={closeMenu}>
                  <Briefcase className="nd1-hiring-nav__icon" aria-hidden="true" />
                  + New job
                </NavLink>
                <NavLink to="/recruitment-profile" className={hiringNavLinkClass} onClick={closeMenu}>
                  <UserRound className="nd1-hiring-nav__icon" aria-hidden="true" />
                  Profile
                </NavLink>
              </div>
              <div className="nd1-hiring-nav__session">{sessionControls}</div>
            </>
          ) : (
            <>
              <Link
                to="/job-seekers"
                className={`nd1-nav__link${isJobSeekersPage ? " nd1-nav__link--active" : ""}`}
                onClick={closeMenu}
              >
                For job seekers
              </Link>
              <Link
                to="/hiring-managers"
                className={`nd1-nav__link nd1-nav__link--p2${
                  isHiringPage ? " nd1-nav__link--active" : ""
                }`}
                onClick={closeMenu}
              >
                For hiring managers
              </Link>

              {hasActiveSession ? (
                <div className="nd1-nav__session">{sessionControls}</div>
              ) : (
                <>
                  <Link
                    to={ND1_SIGNIN}
                    className={`nd1-nav__link nd1-nav__signin${
                      isSigninPage ? " nd1-nav__link--active" : ""
                    }`}
                    onClick={closeMenu}
                  >
                    Sign in
                  </Link>
                  <Link
                    to={signupHref}
                    className="nd1-btn nd1-btn--primary nd1-nav__cta"
                    onClick={closeMenu}
                  >
                    Sign up
                    <ArrowRight className="nd1-icon" aria-hidden="true" />
                  </Link>
                </>
              )}
            </>
          )}
        </div>
      </nav>
    </header>
  );
}
